import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Bot, ExternalLink, Eye, EyeOff, FileText, FolderGit2, GitBranch, History, Pause, Play, Plus, RefreshCw, Rocket, RotateCcw, RotateCw, Save, Square, Trash2, Undo2, Webhook } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { DeploymentTable } from "../components/DeploymentTable.jsx";
import { ManifestForm } from "../components/ManifestForm.jsx";
import { StatusBadge, StatusDot, StatusIcon } from "../components/StatusBadge.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { Elapsed, EmptyState, Menu, Modal, Spinner, Tabs, ago, sessionEnded, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

const TABS = [
    { id: "deployments", label: "Deployments" },
    { id: "processes", label: "Processes" },
    { id: "domains", label: "Domains" },
    { id: "environment", label: "Environment" },
    { id: "settings", label: "Settings" },
    { id: "webhook", label: "Webhook" }
];

export function ProjectDetail() {
    const { slug } = useParams();
    // The tab is part of the address: a link to a project's Environment should
    // open on Environment.
    const [{ tab }, setUrl] = useUrlStates({ tab: "deployments" });
    const setTab = (value) => setUrl("tab", value);
    const [project, setProject] = useState(null);
    const toast = useToast();

    // Separately from the project: idle/busy is what moves, and the sessions
    // watch on the server says when it does.
    const [session, setSession] = useState(null);
    const reloadSession = useCallback(
        () => api.workspaces.get(slug).then(setSession).catch(() => setSession({ workspace: null })),
        [slug]
    );
    useLive(["sessions", "workspaces"], reloadSession, { key: slug });

    const load = useCallback(() => api.projects.get(slug).then(setProject).catch(() => setProject(false)), [slug]);
    // Another project's write is not this page's business.
    const mine = (event) => !event.slug || event.slug === slug;
    useLive(["projects"], (event) => mine(event) && load(), { key: slug });

    // Held here rather than in the tab, because the sticky header shows the
    // newest deployment on every tab — and while one is running, how long for.
    // `GET /projects/:slug` carries no `lastDeployment`; only the list does.
    const [deployments, setDeployments] = useState(null);
    const reloadDeployments = useCallback(
        () => api.deployments.list({ slug, limit: 30 }).then(setDeployments).catch(() => setDeployments([])),
        [slug]
    );
    useLive(["deployments"], (event) => mine(event) && reloadDeployments(), { key: slug });
    const latest = deployments?.[0] ?? null;

    if (project === false) return <EmptyState title="No such project" />;
    if (!project) return <Spinner className="muted" />;

    const act = async (verb, fn) => {
        try {
            await fn();
            toast(`${slug} ${verb}`);
            load();
            reloadDeployments();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    return (
        <div className="space-y-4">
            {/*
              * Sticky, because every tab under it is long enough to scroll and you
              * need to know which project you are acting on when you press Deploy.
              * It carries the status too: a project whose last deploy failed should
              * not look like one that is fine.
              */}
            {/* Opaque, for the same reason as the app bar — see AppShell. */}
            <div className="sticky top-0 z-20 -mx-4 border-b border-gray-200 bg-gray-50 px-4 pt-[max(0.5rem,env(safe-area-inset-top))] sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 dark:border-gray-800 dark:bg-gray-950">
                <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 pb-2">
                    <div className="flex min-w-0 items-center gap-3">
                        <ProjectIcon slug={project.slug} hash={project.favicon?.hash} size={32} framed />
                        <div className="min-w-0">
                            <h1 className="page-title flex items-center gap-2 truncate">
                                {project.slug}
                                {project.state === "draft" ? <span className="badge-queued shrink-0">draft</span> : null}
                                {project.paused ? <span className="badge-warn shrink-0">paused</span> : null}
                            </h1>
                            <p className="muted flex flex-wrap items-center gap-x-2 text-xs">
                                <span className="flex items-center gap-1"><GitBranch size={10} /> {project.gitlab.path} · {project.branch}</span>
                                <span>Node {project.node}</span>
                                {project.domains?.[0] ? (
                                    <a href={`https://${project.domains[0]}`} target="_blank" rel="noreferrer" className="link">{project.domains[0]}</a>
                                ) : null}
                            </p>
                        </div>
                    </div>

                    {/*
                     * Every action in the top-right corner, on the name's line.
                     * A second row of buttons under the title pushed the content
                     * down on a phone and was the first thing to go.
                     */}
                    <div className="flex shrink-0 items-center gap-1">
                        <span className="hidden items-center gap-2 sm:flex">
                            {latest ? <StatusBadge status={latest.status} since={latest.startedAt} /> : null}
                            <Ahead slug={slug} />
                        </span>
                        {/* The phone has no room for the pill; the glyph says the same, and a running one counts beside it. */}
                        {latest ? (
                            <span className="flex items-center gap-1.5 pr-1 sm:hidden">
                                <StatusIcon status={latest.status} />
                                {latest.status === "running" ? <Elapsed since={latest.startedAt} className="text-xs text-blue-600 dark:text-blue-400" /> : null}
                            </span>
                        ) : null}

                        <SessionControl slug={slug} project={project} state={session} reload={reloadSession} toast={toast} />

                        <Menu
                            label="Project actions"
                            items={[
                                {
                                    label: "Deploy",
                                    icon: Rocket,
                                    disabled: project.paused || project.state === "draft",
                                    hint: project.state === "draft"
                                        ? "Still a draft — activate it first"
                                        : project.paused ? "Deploys are paused" : `${project.gitlab.path} · ${project.branch}`,
                                    onSelect: () => act("deploying", () => api.projects.deploy(slug))
                                },
                                {
                                    label: "Roll back",
                                    icon: RotateCcw,
                                    disabled: project.strategy !== "releases" || !project.previousRelease,
                                    hint: project.previousRelease
                                        ? `To ${project.previousRelease.split("/").at(-1)}`
                                        : "No previous release recorded",
                                    onSelect: () => act("rolling back", () => api.projects.rollback(slug))
                                },
                                project.paused
                                    ? { label: "Resume deploys", icon: Play, onSelect: () => act("resumed", () => api.projects.resume(slug)) }
                                    : { label: "Pause deploys", icon: Pause, hint: "A push is recorded but not run", onSelect: () => act("paused", () => api.projects.pause(slug)) }
                            ]}
                        />
                    </div>
                </div>
            </div>

            {project.state === "draft" ? <Draft project={project} workspace={session?.workspace} onChange={load} setTab={setTab} /> : null}

            <Tabs tabs={TABS} active={tab} onChange={setTab} />

            {tab === "deployments" ? <DeploymentsTab slug={slug} deployments={deployments} /> : null}
            {tab === "processes" ? <ProcessesTab project={project} /> : null}
            {tab === "domains" ? <DomainsTab project={project} /> : null}
            {tab === "environment" ? <EnvironmentTab slug={slug} onSaved={load} /> : null}
            {tab === "settings" ? <SettingsTab project={project} onSaved={load} /> : null}
            {tab === "webhook" ? <WebhookTab slug={slug} project={project} /> : null}
        </div>
    );
}

/**
 * How much is waiting to be deployed.
 *
 * Next to the button that would deploy it, because "what will this do" is the
 * question you have at that moment and the answer was previously a leap of faith.
 */
function Ahead({ slug }) {
    const [state, setState] = useState(null);

    // Asked of GitLab, so not on every step a runner saves: once a deploy of
    // this project has landed, the answer has changed and is asked for again.
    useLive(
        ["deployments"],
        (event) => {
            if (event.kind !== "*" && !(event.slug === slug && ["success", "rolled-back"].includes(event.status))) return;
            api.projects.commits(slug).then(setState).catch(() => setState(false));
        },
        { key: slug }
    );

    // Anything unexpected renders nothing. GitLab being unreachable, or answering
    // in a shape this does not know, must not take the project page down with it —
    // which is exactly what an unguarded `state.commits.filter` did.
    const ahead = Array.isArray(state?.commits) ? state.commits.filter((commit) => commit.ahead) : null;
    if (!ahead) return null;
    if (ahead.length === 0) return <span className="muted text-xs">up to date</span>;

    return (
        <span
            className="badge-warn cursor-help"
            title={ahead.map((commit) => `${commit.sha}  ${commit.title}`).join("\n")}
        >
            {ahead.length} commit{ahead.length === 1 ? "" : "s"} to deploy
        </span>
    );
}

/**
 * What a draft is still missing, and the button that ends it.
 *
 * A draft is a project that has never been set up: it has a repository and a
 * manifest being written, no GitLab hook, and it is kept out of the routing table
 * so a push cannot deploy it into existence. Activating is what makes it real.
 */
function Draft({ project, workspace, onChange, setTab }) {
    const [busy, setBusy] = useState(false);
    const toast = useToast();

    const steps = [
        { done: Boolean(workspace), label: "A working copy in ~/dev to work in" },
        { done: (project.services ?? []).length > 0 || (project.steps ?? []).length > 0, label: "A manifest: how it is built and what runs it" },
        { done: (project.domains ?? []).length > 0, label: "A domain, if it serves one" }
    ];

    const activate = async () => {
        setBusy(true);
        try {
            const result = await api.projects.activate(project.slug);
            toast(result.hook?.configured ? `${project.slug} is live; the webhook is set` : `${project.slug} activated, but the webhook is not set`);
            await onChange();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card min-w-0 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="section-title">Not deployed yet</p>
                    <p className="muted mt-0.5 text-sm">
                        A push to <code className="font-mono">{project.gitlab.path}</code> will not deploy this until it is activated. Fill the
                        manifest in on Settings, or ask the Claude session to do it.
                    </p>
                    <ul className="mt-3 space-y-1.5">
                        {steps.map((step) => (
                            <li key={step.label} className="flex items-center gap-2 text-xs">
                                <span className={`size-2 shrink-0 rounded-full ${step.done ? "bg-green-500" : "bg-gray-300 dark:bg-gray-700"}`} aria-hidden />
                                <span className={step.done ? "muted line-through" : ""}>{step.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="flex shrink-0 gap-2">
                    <button className="btn-ghost px-3 py-1.5" onClick={() => setTab("settings")}>
                        <FileText size={13} /> Manifest
                    </button>
                    <button className="btn-primary px-3 py-1.5" onClick={activate} disabled={busy}>
                        {busy ? <Spinner size={13} /> : <Rocket size={13} />} Activate
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * The Claude session, as one button beside the overflow.
 *
 * Tapping it shows the state and offers the one action that makes sense for it,
 * rather than firing something consequential straight off an icon: a bot glyph
 * that silently started an agent would be a bad button.
 *
 * The glyph carries the state on its own, so the corner still answers "is
 * anything running here?" without being opened — muted for none, green for idle,
 * blue and pulsing for busy.
 */
function SessionControl({ slug, project, state, reload, toast }) {
    const [busy, setBusy] = useState(false);
    const workspace = state?.workspace ?? null;
    const session = workspace?.session ?? null;
    // The repository the project is built from is the one to clone: a working copy
    // that is not the project's source would be a session working on the wrong code.
    const repo = project?.sources?.[0]?.url ?? null;

    const tone = !session
        ? "muted"
        : session.status === "busy"
          ? "text-blue-600 dark:text-blue-400"
          : "text-green-600 dark:text-green-400";

    const run = async (verb, call) => {
        setBusy(true);
        try {
            await call();
            toast(`Session ${verb}`);
        } catch (error) {
            toast(error.body?.command ? `${error.message} Run: ${error.body.command}` : error.message, "error");
        } finally {
            setBusy(false);
        }
        await reload();
    };

    return (
        <Menu
            label="Claude session"
            width="w-64"
            trigger={
                <span className="relative flex size-9 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800">
                    <Bot size={17} className={tone} />
                    {session ? (
                        <span
                            className={`absolute right-1.5 top-1.5 size-2 rounded-full ${
                                session.status === "busy" ? "animate-pulse bg-blue-500" : "bg-green-500"
                            }`}
                        />
                    ) : null}
                </span>
            }
        >
            <div className="px-3 py-2">
                <p className="text-sm font-medium">Claude session</p>
                {!state ? (
                    <p className="muted mt-0.5 text-xs">Checking…</p>
                ) : !workspace ? (
                    <p className="muted mt-0.5 text-xs">
                        No working copy at <code className="font-mono">~/dev/{slug}</code> yet.
                    </p>
                ) : session ? (
                    <p className="muted mt-0.5 text-xs">
                        {session.status} · {session.kind} · started {ago(session.startedAt)}
                        <br />
                        <code className="font-mono">{session.cwd.replace(/^\/home\/[^/]+/, "~")}</code>
                    </p>
                ) : workspace?.lastSession ? (
                    <p className="muted mt-0.5 text-xs" title={sessionEnded(workspace.lastSession).why}>
                        {workspace.lastSession.state === "done"
                            ? "None. The last one was retired after eight hours idle — its conversation is kept."
                            : "None. The last one was stopped — its conversation is kept."}
                    </p>
                ) : (
                    <p className="muted mt-0.5 text-xs">
                        None. Would run in <code className="font-mono">~/dev/{slug}</code> and be driven from the Claude app.
                    </p>
                )}
            </div>

            <div className="border-t border-gray-100 pt-1 dark:border-gray-800">
                {!workspace ? (
                    repo ? (
                        <button
                            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
                            disabled={busy}
                            onClick={() => run("ready", () => api.workspaces.clone(slug, repo))}
                        >
                            <FolderGit2 size={14} /> {busy ? "Cloning…" : `Clone to ~/dev/${slug}`}
                        </button>
                    ) : (
                        <p className="muted px-3 py-2 text-xs">This project has no repository to clone.</p>
                    )
                ) : !session ? (
                    <>
                        {/* A retired session is the ordinary end of a day's work, so
                            picking the conversation back up leads. */}
                        {workspace.lastSession ? (
                            <button
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
                                disabled={busy}
                                onClick={() => run("continued", () => api.workspaces.startSession(slug, workspace.lastSession.sessionId))}
                            >
                                <RotateCcw size={14} /> Continue last session
                            </button>
                        ) : null}
                        <button
                            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
                            disabled={busy}
                            onClick={() => run("started", () => api.workspaces.startSession(slug))}
                        >
                            <Play size={14} /> {workspace.lastSession ? "Start a new session" : "Start session"}
                        </button>
                    </>
                ) : session.kind === "background" ? (
                    <button
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-red-700 hover:bg-gray-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-gray-800"
                        disabled={busy}
                        onClick={() => run("stopped", () => api.workspaces.stopSession(slug))}
                    >
                        <Square size={14} /> Stop session
                    </button>
                ) : (
                    <p className="muted px-3 py-2 text-xs">Started in a terminal, so it is stopped there.</p>
                )}
                {workspace ? (
                    <Link
                        to={`/workspaces?history=${slug}`}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                        <History size={14} /> Previous sessions
                    </Link>
                ) : null}
            </div>
        </Menu>
    );
}

function DeploymentsTab({ slug, deployments }) {
    if (!deployments) return <Spinner className="muted" />;
    if (deployments.length === 0) return <EmptyState title="Nothing deployed yet" hint="Push to the branch, or press Deploy." />;
    return <DeploymentTable deployments={deployments} caption={`Deployments for ${slug}`} />;
}

/**
 * The project's own processes, and the buttons you would otherwise go to the
 * Processes page for.
 *
 * A project is several processes and they fail one at a time. Being able to
 * restart the worker without leaving the page you diagnosed it on is most of the
 * value of having a project page at all.
 */
function ProcessesTab({ project }) {
    const [processes, setProcesses] = useState(null);
    const [logs, setLogs] = useState(null);
    const toast = useToast();

    const load = useCallback(
        () =>
            api.system
                .pm2()
                .then((all) => setProcesses(all.filter((proc) => proc.slug === project.slug)))
                .catch(() => setProcesses([])),
        [project.slug]
    );

    // PM2's word moves with the box's sample.
    useLive(["system"], load, { key: project.slug });

    if (!processes) return <Spinner className="muted" />;
    if (processes.length === 0) {
        return (
            <EmptyState
                title="No processes"
                hint={`Nothing in PM2 is tagged to ${project.slug}. The manifest's services name the processes ops reloads after a deploy.`}
            />
        );
    }

    const act = async (name, action) => {
        try {
            await api.system.pm2Action(name, action);
            toast(`${name} ${action}ed`);
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    return (
        <>
            <DataTable
                rows={processes}
                rowKey={(proc) => `${proc.name}-${proc.pid}`}
                caption={`${project.slug} processes`}
                actions={(proc) => [
                    { label: "Reload", icon: RefreshCw, onSelect: () => act(proc.name, "reload") },
                    { label: "Restart", icon: RotateCw, onSelect: () => act(proc.name, "restart") },
                    { label: "Stop", icon: Square, onSelect: () => act(proc.name, "stop"), danger: true },
                    { label: "Logs", icon: FileText, onSelect: () => setLogs(proc.name) }
                ]}
                columns={[
                    {
                        key: "name",
                        header: "Process",
                        primary: true,
                        cell: (proc) => <span className="truncate font-mono text-xs">{proc.name}</span>
                    },
                    {
                        key: "status",
                        header: "Status",
                        summary: true,
                        cell: (proc) => (
                            <>
                                <span className="md:hidden"><StatusDot status={proc.status} title={proc.status} /></span>
                                <span className="hidden md:inline"><StatusBadge status={proc.status} /></span>
                            </>
                        )
                    },
                    { key: "memory", header: "Memory", align: "right", summary: true, cell: (proc) => <span className="tabular-nums">{(proc.memory / 1e6).toFixed(0)} MB</span> },
                    { key: "cpu", header: "CPU", align: "right", cell: (proc) => `${proc.cpu}%` },
                    {
                        key: "restarts",
                        header: "Restarts",
                        align: "right",
                        cell: (proc) => <span className={proc.restarts > 10 ? "text-amber-600 dark:text-amber-400" : ""}>{proc.restarts}</span>
                    },
                    { key: "uptime", header: "Uptime", align: "right", hideBelow: "lg", cell: (proc) => <span className="muted whitespace-nowrap">{proc.uptime ? ago(proc.uptime) : "—"}</span> }
                ]}
            />

            <Modal open={Boolean(logs)} onClose={() => setLogs(null)} title={`${logs} logs`} width="max-w-4xl">
                {logs ? <ProcessLogs name={logs} /> : null}
            </Modal>
        </>
    );
}

function ProcessLogs({ name }) {
    const [text, setText] = useState(null);
    useEffect(() => {
        fetch(api.system.pm2LogsUrl(name)).then((r) => r.text()).then(setText).catch(() => setText("could not read the logs"));
    }, [name]);

    return <pre className="h-96 overflow-auto rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-300">{text ?? "loading…"}</pre>;
}

/**
 * What this project serves, and what nginx does with it.
 *
 * The configuration is shown because "why is it serving that" is a question you
 * ask here, at the moment you notice, and the alternative is ssh.
 */
function DomainsTab({ project }) {
    const [data, setData] = useState(null);
    const [openFile, setOpenFile] = useState(null);

    // Whether each domain answers is the monitor's word, renewed each pass.
    useLive(["status"], () => api.projects.nginx(project.slug).then(setData).catch(() => setData({ domains: [], files: [] })), { key: project.slug });

    if (!data) return <Spinner className="muted" />;
    if (data.domains.length === 0 && data.files.length === 0) {
        return (
            <EmptyState
                title="No domains configured"
                hint="Add them to `domains` in Settings. The first is canonical, and its favicon becomes the project's icon."
            />
        );
    }

    return (
        <div className="space-y-4">
            <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                {data.domains.map((entry) => (
                    <div key={entry.domain} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                        <div className="flex min-w-0 items-center gap-2">
                            <a href={`https://${entry.domain}`} target="_blank" rel="noreferrer" className="link truncate">{entry.domain}</a>
                            <ExternalLink size={11} className="muted shrink-0" aria-hidden />
                            {entry.points === "proxied" ? <span className="badge-queued shrink-0" title="Resolves to a proxy, which terminates TLS itself">proxied</span> : null}
                            {entry.points === "unresolved" ? <span className="badge-failed shrink-0" title="This name does not resolve, so nothing reaches it">no DNS</span> : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-3 text-xs">
                            {entry.upstreams.length ? <span className="muted font-mono tabular-nums">→ {entry.upstreams.join(", ")}</span> : <span className="muted">static</span>}
                            {entry.check
                                ? entry.check.ok
                                    ? <span className="badge-success">{entry.check.status} · {entry.check.ms}ms</span>
                                    : <span className="badge-failed">{entry.check.status ?? "no answer"}</span>
                                : <span className="badge-queued">not checked</span>}
                        </div>
                    </div>
                ))}
            </div>

            {data.files.map((file) => (
                <div key={file.path} className="card overflow-hidden">
                    <button
                        onClick={() => setOpenFile(openFile === file.path ? null : file.path)}
                        aria-expanded={openFile === file.path}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            <FileText size={13} className="muted shrink-0" aria-hidden />
                            <code className="truncate font-mono text-xs">{file.path}</code>
                            {/* Meant to be a symlink into sites-available; a copy diverges silently. */}
                            {file.enabledAsFile ? <span className="badge-warn shrink-0">copy</span> : null}
                        </span>
                        <span className="muted shrink-0 text-xs">{openFile === file.path ? "hide" : "show"}</span>
                    </button>
                    {openFile === file.path ? (
                        <pre className="max-h-[28rem] overflow-auto border-t border-gray-200 bg-gray-900 p-3 font-mono text-xs leading-relaxed text-gray-300 dark:border-gray-800">
                            {file.text}
                        </pre>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function EnvironmentTab({ slug, onSaved }) {
    const [data, setData] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [edits, setEdits] = useState({});
    // `group.KEY` paths marked for removal; like an edit, nothing until saved.
    const [removed, setRemoved] = useState([]);
    const [revisions, setRevisions] = useState([]);
    const toast = useToast();

    const load = useCallback(
        (reveal = false) =>
            api.env
                .get(slug, reveal)
                .then((result) => {
                    setData(result);
                    setRevealed(result.revealed);
                    setEdits({});
                    setRemoved([]);
                })
                .catch((error) => toast(error.message, "error")),
        [slug, toast]
    );

    const loadRevisions = useCallback(() => api.env.revisions(slug).then((r) => setRevisions(r.revisions)).catch(() => {}), [slug]);
    // A revision written elsewhere — a session, the skill — shows up here, but
    // never over something half typed: with edits pending the page keeps them.
    const dirty = Object.keys(edits).length > 0 || removed.length > 0;
    const pending = useRef(dirty);
    pending.current = dirty;
    useLive(
        ["env"],
        (event) => {
            if (event.kind !== "*" && event.slug !== slug) return;
            if (pending.current) return;
            // As it is being looked at: revealed stays revealed across a refetch.
            load(revealed);
            loadRevisions();
        },
        { key: slug }
    );

    if (!data) return <Spinner className="muted" />;

    const groups = Object.entries(data.groups);
    if (groups.length === 0) {
        return <EmptyState title="No environment groups" hint="Add one in Settings — a group is a named .env file, which is how build-time and run-time variables are kept apart." />;
    }

    // A key added here is only an entry in `edits` until it is saved: it is the
    // same "value typed but not yet a revision" as a change to an existing key.
    const addedKeys = (group) =>
        Object.keys(edits)
            .filter((path) => path.startsWith(`${group}.`))
            .map((path) => path.slice(group.length + 1))
            .filter((key) => !data.groups[group].vars.some((item) => item.key === key));

    const isRemoved = (group, key) => removed.includes(`${group}.${key}`);
    const toggleRemoved = (group, key) =>
        setRemoved((previous) => (previous.includes(`${group}.${key}`) ? previous.filter((path) => path !== `${group}.${key}`) : [...previous, `${group}.${key}`]));
    const dropAdded = (group, key) =>
        setEdits((previous) => Object.fromEntries(Object.entries(previous).filter(([path]) => path !== `${group}.${key}`)));

    // A saved change is only on disk after a deploy or a restart, so the toast
    // offers both: a restart when some running process reads what changed, and
    // a redeploy always — a group read only by the build needs one.
    const offerToApply = (rev, restartable) => {
        const groups = Object.keys(restartable).filter((name) => restartable[name].length > 0);
        const services = [...new Set(groups.flatMap((name) => restartable[name]))];
        const buildOnly = Object.keys(restartable).filter((name) => restartable[name].length === 0).map((name) => data.groups[name].file);

        const run = (verb, fn) => async () => {
            try {
                const result = await fn();
                toast(`${slug} #${result.number} ${verb}`);
                onSaved?.();
            } catch (error) {
                toast(error.message, "error");
            }
        };
        const actions = [
            services.length
                ? { label: services.length === 1 ? `Restart ${services[0]}` : `Restart ${services.length} processes`, icon: RotateCw, run: run("restarting", () => api.projects.restart(slug, groups)) }
                : null,
            { label: "Redeploy", icon: Rocket, run: run("deploying", () => api.projects.deploy(slug)) }
        ].filter(Boolean);

        const note = services.length === 0
            ? "takes effect on the next deploy"
            : buildOnly.length
                ? `${buildOnly.join(", ")} is only read by the build — redeploy for that`
                : "apply it now?";
        toast(`Saved as revision ${rev} — ${note}`, "info", { actions });
    };

    const save = async () => {
        const payload = {};
        for (const [group, vars] of Object.entries(data.groups)) {
            payload[group] = {};
            for (const key of addedKeys(group)) payload[group][key] = edits[`${group}.${key}`];
            for (const item of vars.vars) {
                if (isRemoved(group, item.key)) continue;
                const edited = edits[`${group}.${item.key}`];
                if (edited !== undefined) payload[group][item.key] = edited;
                else if (!item.masked) payload[group][item.key] = item.value;
                else if (!item.missing) {
                    // Untouched masked values must be carried forward unchanged; the
                    // API needs a value, so an unrevealed save is refused instead of
                    // silently writing the mask into the file.
                    toast("Reveal values before saving, or only edit the ones you change after revealing", "error");
                    throw new Error("cannot save masked values");
                }
            }
        }
        try {
            const result = await api.env.put(slug, { groups: payload });
            offerToApply(result.rev, result.restartable ?? {});
            load(revealed);
            onSaved?.();
        } catch (error) {
            if (error.message !== "cannot save masked values") toast(error.message, "error");
        }
    };

    return (
        // `data-dirty` while there are edits: a new build must not reload the tab over them.
        <div className="space-y-4" data-dirty={dirty ? "" : undefined}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs muted">
                    Revision {data.currentRev}. Values are sealed at rest and take effect on the next deploy or restart.
                </p>
                <div className="flex gap-2">
                    <button className="btn-ghost" onClick={() => load(!revealed)}>
                        {revealed ? <EyeOff size={14} /> : <Eye size={14} />} {revealed ? "Hide" : "Reveal"}
                    </button>
                    <button className="btn-primary" onClick={save} disabled={!dirty}>
                        <Save size={14} /> Save revision
                    </button>
                </div>
            </div>

            {groups.map(([name, group]) => (
                <div key={name} className="card overflow-hidden">
                    <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-800">
                        <span className="font-mono text-xs font-medium">{group.file}</span>
                        <span className="text-xs muted">{group.vars.length} keys</span>
                    </div>
                    <table className="w-full">
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {group.vars.map((item) => {
                                const gone = isRemoved(name, item.key);
                                return (
                                    <tr key={item.key} className={`block md:table-row ${item.missing ? "bg-red-50/50 dark:bg-red-950/20" : ""}`}>
                                        <td className="table-data block font-mono text-xs md:table-cell md:w-1/3">
                                            <span className={gone ? "muted line-through" : ""}>{item.key}</span>
                                            {item.missing ? <span className="ml-2 badge-failed">required</span> : null}
                                            {item.placeholder ? <span className="ml-2 badge-warn">placeholder</span> : null}
                                            {gone ? <span className="ml-2 badge-failed">removed</span> : null}
                                        </td>
                                        <td className="table-data block pt-0 md:table-cell md:pt-2">
                                            {/*
                                              * Controlled, not `defaultValue`. React reads a
                                              * defaultValue once, on mount — so revealing
                                              * re-fetched the real values, put them in state,
                                              * and the inputs went on showing the mask. The
                                              * button appeared to do nothing at all.
                                              */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    className={`input-mono ${gone ? "opacity-50" : ""}`}
                                                    value={edits[`${name}.${item.key}`] ?? item.value ?? ""}
                                                    placeholder={item.missing ? "not set — the deploy will fail" : ""}
                                                    disabled={gone}
                                                    onChange={(event) => setEdits((previous) => ({ ...previous, [`${name}.${item.key}`]: event.target.value }))}
                                                />
                                                {/* A required key is the manifest's to drop, not this form's. */}
                                                {group.required.includes(item.key) ? null : (
                                                    <RemoveKey gone={gone} name={item.key} onClick={() => toggleRemoved(name, item.key)} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {addedKeys(name).map((key) => (
                                <tr key={`new.${key}`} className="block md:table-row">
                                    <td className="table-data block font-mono text-xs md:table-cell md:w-1/3">
                                        {key}
                                        <span className="ml-2 badge-warn">new</span>
                                    </td>
                                    <td className="table-data block pt-0 md:table-cell md:pt-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                className="input-mono"
                                                autoFocus
                                                value={edits[`${name}.${key}`]}
                                                onChange={(event) => setEdits((previous) => ({ ...previous, [`${name}.${key}`]: event.target.value }))}
                                            />
                                            <RemoveKey name={key} onClick={() => dropAdded(name, key)} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <AddEnvKey
                        taken={[...group.vars.map((item) => item.key), ...addedKeys(name)]}
                        onAdd={(key) => setEdits((previous) => ({ ...previous, [`${name}.${key}`]: "" }))}
                    />
                </div>
            ))}

            {revisions.length > 0 ? (
                <div className="card p-3">
                    <p className="mb-2 text-xs font-medium text-gray-600 dark:muted">History</p>
                    <ul className="space-y-1 text-xs">
                        {revisions.slice(0, 8).map((revision) => (
                            <li key={revision.rev} className="flex items-center justify-between gap-2">
                                <span>
                                    rev {revision.rev} · {revision.createdBy} · {ago(revision.createdAt)}
                                    {revision.note ? <span className="muted"> — {revision.note}</span> : null}
                                </span>
                                {revision.rev !== data.currentRev ? (
                                    <button
                                        className="link"
                                        onClick={async () => {
                                            await api.env.revert(slug, revision.rev);
                                            toast(`Reverted to revision ${revision.rev}`);
                                            load(revealed);
                                        }}
                                    >
                                        revert
                                    </button>
                                ) : (
                                    <span className="muted">current</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

function RemoveKey({ gone = false, name, onClick }) {
    const Icon = gone ? Undo2 : Trash2;
    return (
        <button
            type="button"
            className="muted flex size-9 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800 dark:hover:text-red-400"
            aria-label={gone ? `Keep ${name}` : `Remove ${name}`}
            title={gone ? "Keep" : "Remove"}
            onClick={onClick}
        >
            <Icon size={15} />
        </button>
    );
}

const ENV_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

function AddEnvKey({ taken, onAdd }) {
    const [key, setKey] = useState("");
    const name = key.trim();
    const problem = !name ? null : !ENV_KEY.test(name) ? "letters, digits and _ only" : taken.includes(name) ? "already in this file" : null;

    const add = (event) => {
        event.preventDefault();
        if (!name || problem) return;
        onAdd(name);
        setKey("");
    };

    return (
        <form onSubmit={add} className="flex flex-wrap items-center gap-2 border-t border-gray-200 px-3 py-2 dark:border-gray-800">
            <input className="input-mono max-w-xs" placeholder="NEW_KEY" value={key} onChange={(event) => setKey(event.target.value)} />
            <button type="submit" className="btn-ghost" disabled={!name || Boolean(problem)}>
                <Plus size={14} /> Add variable
            </button>
            {problem ? <span className="text-xs text-red-600 dark:text-red-400">{problem}</span> : null}
        </form>
    );
}

function SettingsTab({ project, onSaved }) {
    const [problems, setProblems] = useState([]);
    const toast = useToast();

    const save = async (draft) => {
        try {
            const result = await api.projects.update(project.slug, draft);
            setProblems([]);
            toast(result.changed.length ? `Updated ${result.changed.join(", ")}` : "No changes");
            onSaved?.();
        } catch (error) {
            setProblems(error.body?.problems ?? [{ path: "", message: error.message }]);
        }
    };

    // Keyed on the manifest revision so a save that changes the shape re-seeds the
    // form from what the server actually stored, rather than from the draft.
    return <ManifestForm key={project.manifestRev ?? 0} project={project} problems={problems} onSave={save} />;
}

function WebhookTab({ slug, project }) {
    const [status, setStatus] = useState(null);
    const toast = useToast();

    const load = useCallback(() => api.projects.webhook(slug).then(setStatus).catch((error) => setStatus({ error: error.message })), [slug]);
    useEffect(() => {
        load();
    }, [load]);

    if (!status) return <Spinner className="muted" />;

    const repair = async (rotate = false) => {
        try {
            const result = await api.projects.configureWebhook(slug, { rotate });
            toast(result.created ? "Webhook created" : rotate ? `Signing key rotated to v${result.signingKeyVersion}` : "Webhook repaired");
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    return (
        <div className="space-y-3">
            <div className="card divide-y divide-gray-100 p-0 text-sm dark:divide-gray-800">
                <Row label="Endpoint"><code className="font-mono text-xs">{status.expectedUrl}</code></Row>
                <Row label="Branch filter">
                    {status.branchFilter ?? "—"}
                    {status.present && status.branchFilter !== (status.expectedFilter ?? project.branch) ? (
                        <span className="ml-2 badge-warn">expected {status.expectedFilter ?? project.branch}</span>
                    ) : null}
                    {status.sharedWith?.length ? (
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">shared with {status.sharedWith.join(", ")}</span>
                    ) : null}
                </Row>
                <Row label="In GitLab">
                    {status.error ? <span className="badge-failed">{status.error}</span>
                        : status.present ? <StatusBadge status={status.pointsHere ? "success" : "failed"}>{status.pointsHere ? "points here" : "points elsewhere"}</StatusBadge>
                        : <span className="badge-failed">not configured</span>}
                </Row>
                <Row label="SSL verification">{status.sslVerification ? "on" : <span className="badge-warn">off</span>}</Row>
                <Row label="GitLab state">
                    {status.disabled ? <span className="badge-failed">disabled by GitLab ({status.alertStatus})</span> : status.alertStatus ?? "—"}
                </Row>
                <Row label="Signing key">v{project.gitlab.signingKeyVersion ?? 1} · derived, never stored</Row>
            </div>
            <div className="flex gap-2">
                <button className="btn-primary" onClick={() => repair(false)}><Webhook size={14} /> Create or repair</button>
                <button className="btn-ghost" onClick={() => repair(true)} title="Issues a new signing key and pushes it to GitLab. The previous key keeps working for in-flight deliveries.">
                    Rotate signing key
                </button>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-4 px-3 py-2">
            <span className="text-xs muted">{label}</span>
            <span className="text-right">{children}</span>
        </div>
    );
}
